const CONSENT_STORAGE_KEY = 'cookieConsent';

const DEFAULT_CONSENT = {
  necessary: true,
  functional: false,
  analytics: true,
  marketing: false,
};

/**
 * @returns {{ necessary: boolean, functional: boolean, analytics: boolean, marketing: boolean } | null}
 */
export function getStoredConsent() {
  if (typeof window === 'undefined') {
    return null;
  }
  try {
    const raw = localStorage.getItem(CONSENT_STORAGE_KEY);
    if (!raw) {
      return null;
    }
    const parsed = JSON.parse(raw);
    return {
      necessary: true,
      functional: Boolean(parsed.functional),
      analytics: Boolean(parsed.analytics),
      marketing: Boolean(parsed.marketing),
    };
  } catch {
    return null;
  }
}

export function hasAnalyticsConsent() {
  // Opt-out model: analytics is granted by default and only stops firing
  // when the user has explicitly stored a denied analytics consent.
  const stored = getStoredConsent();
  if (!stored) {
    return DEFAULT_CONSENT.analytics === true;
  }
  return stored.analytics === true;
}

export function hasMarketingConsent() {
  return getStoredConsent()?.marketing === true;
}

/**
 * Apply Consent Mode v2 flags to gtag based on stored or provided consent.
 * @param {{ analytics?: boolean, marketing?: boolean }} consent
 */
export function applyConsentToGtag(consent) {
  if (typeof window === 'undefined' || !window.gtag) {
    return;
  }

  const analyticsGranted = consent.analytics === true;
  const marketingGranted = consent.marketing === true;

  window.gtag('consent', 'update', {
    analytics_storage: analyticsGranted ? 'granted' : 'denied',
    ad_storage: marketingGranted ? 'granted' : 'denied',
    ad_user_data: marketingGranted ? 'granted' : 'denied',
    ad_personalization: marketingGranted ? 'granted' : 'denied',
  });
}

/**
 * Run callback once stored consent exists (banner dismissed or prior visit).
 * @param {(consent: NonNullable<ReturnType<typeof getStoredConsent>>) => void} callback
 */
export function onConsentReady(callback) {
  const consent = getStoredConsent();
  if (consent) {
    callback(consent);
  }
}

export { DEFAULT_CONSENT, CONSENT_STORAGE_KEY };
